// Single owner of Ezoic ad requests for the SPA.
//
// Why ad components must be able to trigger a request, and not just the router:
// most SPA pages render their slots only AFTER an async fetch resolves. Profile
// is the clearest case — ProfileContainer returns <Loading /> until the user
// request comes back, so at route-change time the page is a spinner with no ad
// placeholders in the DOM at all. A request fired only on route change lands
// then, finds nothing, and the slots that mount a moment later are never
// requested: no ads, and Ezoic is left with nothing defined, which surfaces as
//   "No placeholder Ids are currently defined for Refresh()."
//
// So: the router owns teardown, and anything that renders a slot asks for a
// request when it mounts. showAds() re-scans id placeholders and
// showAds('.ezoicad') skips slots that already hold an ad, so extra calls are
// safe — Ezoic documents this as the dynamic-content pattern. Debounced so a
// burst of components mounting together produces a single batched request.

let pending = null

// One bare showAds() per pageload is enough to activate the overlay units
// (anchor etc.) on pages with zero display slots. Repeating it with nothing
// defined makes sa.min.js route the call to Refresh() over an empty registry,
// which both logs "No placeholder Ids are currently defined for Refresh()"
// and serves nothing. Reset on teardown so overlays reactivate after a route
// change exactly as before.
let overlaysActivated = false

export function requestEzoicAds (delay = 150) {
  if (typeof window === 'undefined') { return }
  // Ad-free tiers never get the SDK script, so nothing would ever drain the
  // queue and every mount would leak another closure. Gate on the flag the
  // layout sets alongside the script — NOT on window.ezstandalone.showAds,
  // which is legitimately absent until the async sa.min.js lands. That is the
  // whole reason ezstandalone.cmd exists.
  if (!window.__cccAdsEnabled) { return }

  window.ezstandalone = window.ezstandalone || {}
  window.ezstandalone.cmd = window.ezstandalone.cmd || []

  if (pending) { clearTimeout(pending) }
  pending = setTimeout(function () {
    pending = null
    window.ezstandalone.cmd.push(function () {
      // Decide WHAT to request from the DOM at fire time, and make exactly the
      // call(s) that have something to act on. The old version always fired
      // showAds() AND showAds('.ezoicad') back to back; sa.min.js debounces
      // consecutive showAds calls, so on a page with only class-based slots
      // (logged-out find_jobs) the bare call defined nothing and the selector
      // call was collapsed into a Refresh() of an empty registry — warning
      // logged, define never ran, NO ads served.
      const classSlots = document.querySelector('.ezoicad')
      const idSlots = document.querySelector('[id^="ezoic-pub-ad-placeholder-"]')
      if (classSlots && idSlots) {
        overlaysActivated = true
        window.ezstandalone?.showAds?.()
        window.ezstandalone?.showAds?.('.ezoicad')
      } else if (classSlots) {
        overlaysActivated = true
        window.ezstandalone?.showAds?.('.ezoicad')
      } else if (idSlots || !overlaysActivated) {
        overlaysActivated = true
        window.ezstandalone?.showAds?.()
      }
      // No slots and overlays already running: nothing to do — deliberately
      // silent instead of poking Ezoic into logging refresh warnings.
    })
  }, delay)
}

// Route-change teardown. Ezoic's documented cleanup when placeholder ids can't
// be enumerated. Always pair with a requestEzoicAds() so the new route's slots
// get defined again — destroying without redefining is what produced the
// Refresh() warning above.
export function destroyEzoicAds () {
  if (typeof window === 'undefined') { return }

  // Cancel any not-yet-fired request from the route we're leaving. Otherwise a
  // navigation inside the debounce window lets the stale showAds() land AFTER
  // this teardown and repaint ads on a route that never asked for any.
  if (pending) { clearTimeout(pending); pending = null }

  // Post-destroy, the next route must be allowed one bare showAds() even if it
  // has no display slots, so overlay units come back exactly as they always
  // did after teardown.
  overlaysActivated = false

  window.ezstandalone = window.ezstandalone || {}
  window.ezstandalone.cmd = window.ezstandalone.cmd || []
  window.ezstandalone.cmd.push(function () {
    window.ezstandalone?.destroyAll?.()
  })
}
